const MODULE_ORDER = ["prior_auth", "intake", "bv", "medical_necessity"];
const CHIP_MODULES = new Set(["prior_auth", "intake"]);
const INTAKE_STEP_STATUS = {
    bv: "QUALIFY_PATIENT",
    medical_necessity: "CLINICAL_REVIEW",
};
const STEP_TYPES = new Set(["bv", "medical_necessity"]);
const INTAKE_TYPES = new Set(["intake", "bv", "medical_necessity"]);
const SECTION_LABELS = {
    all: "Worklist",
    prior_auth: "Prior Auth",
    intake: "Intake",
    bv: "Benefit Verification",
    medical_necessity: "Medical Necessity",
};
const CHIP_LABELS = {
    prior_auth: "Prior Auth",
    intake: "Intake",
    bv: "BV",
    medical_necessity: "Med Necessity",
};
const SECTION_ICONS = {
    all: "mdi-format-list-checks",
    prior_auth: "mdi-file-document-check-outline",
    intake: "mdi-tray-arrow-down",
    bv: "mdi-shield-check-outline",
    medical_necessity: "mdi-stethoscope",
};
const INTAKE_MODULES = ["fax_intake", "order_qualification", "clinical_review", "necessity_check"];
const STEP_MODULES = ["order_qualification", "clinical_review"];
const PRIOR_AUTH_PAGES = new Set(["prior-auth", "all-submissions"]);
function showsIntake(modules) {
    return modules.some((key) => INTAKE_MODULES.includes(key));
}
/** Two or more of Prior Auth, Intake, BV and MN get the Worklist; with one, that module's own page is the page. */
export function worklistAppliesTo(modules) {
    const steps = STEP_MODULES.filter((key) => modules.includes(key)).length;
    return Number(modules.includes("prior_auth")) + Number(showsIntake(modules)) + steps >= 2;
}
/** The Worklist is on for this company: the pilot flag plus two or more modules. */
export function worklistShownFor(enabled, modules) {
    return Boolean(enabled) && worklistAppliesTo(modules ?? []);
}
/** The company's home: its configured page, or Intake when that page is Prior Auth and Prior Auth is off. */
export function homePageFor(configured, modules) {
    const page = configured || "prior-auth";
    return PRIOR_AUTH_PAGES.has(page) && !!modules?.length && !modules.includes("prior_auth") ? "intake" : page;
}
/** Prior auth, intake or both, from the modules the company shows. */
export function companyAudienceFor(modules) {
    const intake = showsIntake(modules);
    const pa = modules.includes("prior_auth");
    if (pa && !intake)
        return "prior_auth";
    if (intake && !pa)
        return "intake";
    return "all";
}
/** The work types a Worklist URL is narrowed to, in a fixed order; none means all work. */
export function typesFrom(type) {
    const picked = typeof type === "string" ? type.split(",") : [];
    return MODULE_ORDER.filter((key) => picked.includes(key));
}
/** The layout a Worklist URL shows: a single-module company's own, else the one tab its work types add up to. */
export function audienceFor(company, type) {
    if (company !== "all")
        return company;
    const types = typesFrom(type);
    if (types.length === 1 && types[0] === "prior_auth")
        return "prior_auth";
    if (types.length && types.every((key) => INTAKE_TYPES.has(key)))
        return "intake";
    return "all";
}
/** Toggle one module chip; Intake already holds BV and Medical Necessity, so it and they replace each other. */
export function toggledTypes(current, key) {
    if (current.includes(key))
        return current.filter((type) => type !== key);
    const kept = current.filter((type) => key === "intake" ? !STEP_TYPES.has(type) : !STEP_TYPES.has(key) || type !== "intake");
    return MODULE_ORDER.filter((type) => type === key || kept.includes(type));
}
function section(key, query) {
    return { key, label: SECTION_LABELS[key], icon: SECTION_ICONS[key], query };
}
/** The fan-out under Worklist: every distinct view the company has, the general list first. */
export function sectionsFor(modules) {
    const company = companyAudienceFor(modules);
    const intakeType = company === "all" ? { type: "intake" } : {};
    const sections = [section("all", {})];
    if (company === "all")
        sections.push(section("prior_auth", { type: "prior_auth" }), section("intake", intakeType));
    if (company !== "prior_auth") {
        if (modules.includes("order_qualification")) {
            sections.push(section("bv", { ...intakeType, intake_status: INTAKE_STEP_STATUS.bv }));
        }
        if (modules.includes("clinical_review")) {
            sections.push(section("medical_necessity", { ...intakeType, intake_status: INTAKE_STEP_STATUS.medical_necessity }));
        }
    }
    return sections;
}
/** The header's module chips: Prior Auth and Intake only for now (BV and Medical Necessity stay Intake statuses). */
export function chipSectionsFor(modules) {
    return sectionsFor(modules).flatMap((entry) => entry.key !== "all" && CHIP_MODULES.has(entry.key) ? [{ key: entry.key, label: CHIP_LABELS[entry.key] }] : []);
}
/** Which fan-out entry a Worklist URL is showing. */
export function activeSectionFor(company, query) {
    const audience = audienceFor(company, query.type);
    if (audience === "intake" && !query.pa_status) {
        const step = Object.keys(INTAKE_STEP_STATUS).find((key) => query.intake_status === INTAKE_STEP_STATUS[key]);
        if (step)
            return step;
    }
    return company === "all" ? audience : "all";
}
export function sectionLabel(key) {
    return SECTION_LABELS[key];
}
